import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Badge, Input, Label, Select, SelectItem } from '@fyos/components';
import { Pencil, X, Trash2, Columns3, Calendar, Activity, ThumbsUp, ThumbsDown, ShieldCheck, Users, Link2, AlertTriangle, ExternalLink, Paperclip, Upload, SmilePlus, Eye, EyeOff, MessageSquare, PanelRightClose, Maximize2 } from 'lucide-react';
import { Item, Category, Tag, ItemReaction, itemApi } from '@/lib/itemApi';
import EmojiPicker from './EmojiPicker';
import { useAuthStore } from '@/stores/authStore';
import { attachmentApi, Attachment, formatFileSize, isImageMime } from '@/lib/attachmentApi';
import CommentSection from './CommentSection';
import RichTextEditor from './RichTextEditor';
import MarkdownContent from './MarkdownContent';
import ConfirmDialog from './ConfirmDialog';

interface ItemDetailModalProps {
  workspaceId: string;
  item: Item;
  categories: Category[];
  tags: Tag[];
  onClose: () => void;
  onUpdate: (item: Item) => void;
  onDelete?: (itemId: string) => void;
}

export default function ItemDetailModal({
  workspaceId,
  item,
  categories,
  tags,
  onClose,
  onUpdate,
  onDelete,
}: ItemDetailModalProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(item.title);
  const [description, setDescription] = useState(item.description || '');
  const [categoryId, setCategoryId] = useState<string | null>(item.category_id || null);
  const [selectedTags, setSelectedTags] = useState<string[]>(
    item.tags?.map(t => t.id) || []
  );
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [reactions, setReactions] = useState<ItemReaction[]>(item.reactions || []);
  const { user } = useAuthStore();
  const [isWatching, setIsWatching] = useState(
    item.watchers?.some(w => w.id === user?.id) || false
  );
  const [watcherCount, setWatcherCount] = useState(item.watcher_count || 0);
  const [showComments, setShowComments] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    attachmentApi.getAll(workspaceId, item.id).then(setAttachments).catch(() => {});
    return () => {
      document.body.style.overflow = 'unset';
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose, workspaceId, item.id]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploading(true);
      const attachment = await attachmentApi.upload(workspaceId, item.id, file);
      setAttachments(prev => [attachment, ...prev]);
    } catch (err) {
      console.error('Failed to upload file:', err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    try {
      await attachmentApi.delete(workspaceId, item.id, attachmentId);
      setAttachments(prev => prev.filter(a => a.id !== attachmentId));
    } catch (err) {
      console.error('Failed to delete attachment:', err);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const updated = await itemApi.update(workspaceId, item.id, {
        title,
        description: description || undefined,
        category_id: categoryId || undefined,
        tag_ids: selectedTags.length > 0 ? selectedTags : undefined,
      });
      onUpdate(updated);
      setIsEditing(false);
    } catch (err) {
      console.error('Failed to update item:', err);
    } finally {
      setSaving(false);
    }
  };

  const toggleTag = (tagId: string) => {
    setSelectedTags(prev =>
      prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
    );
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setTitle(item.title);
    setDescription(item.description || '');
    setCategoryId(item.category_id || null);
    setSelectedTags(item.tags?.map(t => t.id) || []);
  };

  const category = categories.find(c => c.id === item.category_id);

  const columnColors: Record<string, { bg: string; text: string }> = {
    now: { bg: 'bg-amber-50 dark:bg-amber-950/30', text: 'text-amber-500' },
    next: { bg: 'bg-blue-50 dark:bg-blue-950/30', text: 'text-blue-500' },
    later: { bg: 'bg-violet-50 dark:bg-violet-950/30', text: 'text-violet-500' },
    shipped: { bg: 'bg-emerald-50 dark:bg-emerald-950/30', text: 'text-emerald-500' },
    killed: { bg: 'bg-gray-100 dark:bg-gray-700/50', text: 'text-gray-500' },
  };
  const colColor = columnColors[item.board_column] || columnColors.killed;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div
        className="fixed inset-0 bg-gray-400/50 dark:bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="relative z-50 w-full transition-[max-width] duration-300 ease-in-out"
        style={{ maxWidth: showComments ? '1100px' : '768px' }}
        role="dialog"
        aria-modal="true"
      >
        <div className="relative flex max-h-[85vh] rounded-2xl bg-white shadow-xl dark:bg-gray-800 overflow-hidden">
        {/* Main content */}
        <div className="flex-1 min-w-0 p-6 sm:p-8 overflow-y-auto">
          {/* Close button */}
          <button
            type="button"
            onClick={onClose}
            className="absolute right-4 top-4 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600 dark:hover:text-white"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>

          {/* Header */}
          <div className="mb-4 pr-10">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{item.title}</h2>
          </div>

          {!isEditing && (
            <div className="mb-4 flex flex-wrap items-center justify-center gap-1">
              <Button
                variant={isWatching ? 'primary' : 'secondary'}
                size="sm"
                iconOnlyOnMobile
                onClick={async () => {
                  try {
                    const result = isWatching
                      ? await itemApi.unwatch(workspaceId, item.id)
                      : await itemApi.watch(workspaceId, item.id);
                    if (result) {
                      setIsWatching(result.watching ?? !isWatching);
                      setWatcherCount(result.watcher_count ?? watcherCount);
                      onUpdate({ ...item, watcher_count: result.watcher_count });
                    } else {
                      setIsWatching(!isWatching);
                    }
                  } catch (err) {
                    console.error('Failed to toggle watch:', err);
                  }
                }}
              >
                {isWatching ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                {isWatching ? 'Unwatch' : 'Watch'}{watcherCount > 0 ? ` (${watcherCount})` : ''}
              </Button>
              <Button variant="secondary" size="sm" iconOnlyOnMobile onClick={() => setIsEditing(true)}>
                <Pencil className="h-3.5 w-3.5" />
                Edit
              </Button>
              {onDelete && (
                <Button
                  variant="danger"
                  size="sm"
                  iconOnlyOnMobile
                  disabled={deleting}
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  {deleting ? 'Deleting...' : 'Delete'}
                </Button>
              )}
              <div className="h-5 w-px bg-gray-200 dark:bg-gray-700 mx-0.5" />
              <Button
                variant={showComments ? 'primary' : 'secondary'}
                size="sm"
                iconOnlyOnMobile
                onClick={() => setShowComments(!showComments)}
              >
                <MessageSquare className="h-3.5 w-3.5" />
                {showComments ? 'Hide' : 'Comments'}{item.comment_count > 0 ? ` (${item.comment_count})` : ''}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                iconOnlyOnMobile
                onClick={() => {
                  onClose();
                  navigate(`/roadmap/${workspaceId}/item/${item.id}`);
                }}
                title="Open full page"
              >
                <Maximize2 className="h-3.5 w-3.5" />
                Full Page
              </Button>
            </div>
          )}

          <div>
            {isEditing ? (
              <div className="space-y-4 mb-6">
                <div>
                  <Label htmlFor="edit-title">Title</Label>
                  <Input
                    id="edit-title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                </div>

                <div>
                  <Label htmlFor="edit-description">Description</Label>
                  <RichTextEditor
                    content={description}
                    onChange={setDescription}
                    placeholder="Add a description..."
                    minHeight="250px"
                  />
                </div>

                {categories.length > 0 && (
                  <div>
                    <Label>Category</Label>
                    <Select
                      value={categoryId || '__none__'}
                      onValueChange={(val) => setCategoryId(val === '__none__' ? null : val)}
                      placeholder="Select category..."
                    >
                      <SelectItem value="__none__">None</SelectItem>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </Select>
                  </div>
                )}

                {tags.length > 0 && (
                  <div>
                    <Label>Tags</Label>
                    <div className="flex flex-wrap gap-2">
                      {tags.map(tag => (
                        <button
                          key={tag.id}
                          type="button"
                          onClick={() => toggleTag(tag.id)}
                        >
                          <Badge
                            variant={selectedTags.includes(tag.id) ? 'primary' : 'gray'}
                            size="md"
                            className="cursor-pointer transition-colors"
                          >
                            {tag.name}
                          </Badge>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-3 justify-end">
                  <Button variant="outline" onClick={cancelEditing}>
                    Cancel
                  </Button>
                  <Button onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="mb-6">
                {item.description && (
                  <MarkdownContent
                    content={item.description}
                    className="text-gray-700 dark:text-gray-300 mb-4 prose prose-sm dark:prose-invert max-w-none"
                  />
                )}

                {(category || (item.tags && item.tags.length > 0)) && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {category && (
                      <Badge
                        size="md"
                        style={{ backgroundColor: category.color + '20', color: category.color }}
                      >
                        {category.name}
                      </Badge>
                    )}
                    {item.tags && item.tags.map(tag => (
                      <Badge
                        key={tag.id}
                        size="md"
                        style={{ backgroundColor: tag.color + '20', color: tag.color }}
                      >
                        {tag.name}
                      </Badge>
                    ))}
                  </div>
                )}

                <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-4">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="flex items-center gap-2">
                      <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${colColor.bg}`}>
                        <Columns3 className={`h-4 w-4 ${colColor.text}`} />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Column</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white capitalize">{item.board_column}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-950/30">
                        <Calendar className="h-4 w-4 text-blue-500" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Created</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{new Date(item.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    {item.last_activity_at && (
                      <div className="flex items-center gap-2">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-green-50 dark:bg-green-950/30">
                          <Activity className="h-4 w-4 text-green-500" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Last Activity</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{new Date(item.last_activity_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                    )}
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            const result = await itemApi.vote(workspaceId, item.id, 'up');
                            onUpdate({ ...item, vote_count: result.vote_count, user_vote: result.user_vote });
                          } catch (err) {
                            console.error('Failed to vote:', err);
                          }
                        }}
                        className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors hover:bg-gray-100 dark:hover:bg-gray-700 ${item.user_vote === 1 ? 'bg-brand-50 dark:bg-brand-950/30 ring-1 ring-brand-300 dark:ring-brand-600' : 'bg-purple-50 dark:bg-purple-950/30'}`}
                        title={item.user_vote === 1 ? 'Remove upvote' : 'Upvote'}
                      >
                        <ThumbsUp className={`h-4 w-4 ${item.user_vote === 1 ? 'text-brand-500' : 'text-purple-500'}`} />
                      </button>
                      <div className="text-center px-1">
                        <p className="text-xs text-gray-500 dark:text-gray-400">Votes</p>
                        <p className={`text-sm font-medium ${item.user_vote === 1 ? 'text-brand-500' : item.user_vote === -1 ? 'text-red-500' : 'text-gray-900 dark:text-white'}`}>{item.vote_count}</p>
                      </div>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            const result = await itemApi.vote(workspaceId, item.id, 'down');
                            onUpdate({ ...item, vote_count: result.vote_count, user_vote: result.user_vote });
                          } catch (err) {
                            console.error('Failed to vote:', err);
                          }
                        }}
                        className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors hover:bg-gray-100 dark:hover:bg-gray-700 ${item.user_vote === -1 ? 'bg-red-50 dark:bg-red-950/30 ring-1 ring-red-300 dark:ring-red-600' : 'bg-purple-50 dark:bg-purple-950/30'}`}
                        title={item.user_vote === -1 ? 'Remove downvote' : 'Downvote'}
                      >
                        <ThumbsDown className={`h-4 w-4 ${item.user_vote === -1 ? 'text-red-500' : 'text-purple-500'}`} />
                      </button>
                    </div>
                  </div>
                  {item.auto_kill_exempt ? (
                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
                        <ShieldCheck className="h-4 w-4" />
                        <span className="text-sm font-medium">Auto-kill exempt</span>
                      </div>
                    </div>
                  ) : null}
                </div>

                {(item.description?.length || 0) > 10240 && (
                  <div className="mt-6 flex flex-wrap items-center justify-center gap-1">
                    <Button
                      variant={isWatching ? 'primary' : 'secondary'}
                      size="sm"
                      iconOnlyOnMobile
                      onClick={async () => {
                        try {
                          const result = isWatching
                            ? await itemApi.unwatch(workspaceId, item.id)
                            : await itemApi.watch(workspaceId, item.id);
                          if (result) {
                            setIsWatching(result.watching ?? !isWatching);
                            setWatcherCount(result.watcher_count ?? watcherCount);
                            onUpdate({ ...item, watcher_count: result.watcher_count });
                          } else {
                            setIsWatching(!isWatching);
                          }
                        } catch (err) {
                          console.error('Failed to toggle watch:', err);
                        }
                      }}
                    >
                      {isWatching ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                      {isWatching ? 'Unwatch' : 'Watch'}{watcherCount > 0 ? ` (${watcherCount})` : ''}
                    </Button>
                    <Button variant="secondary" size="sm" iconOnlyOnMobile onClick={() => setIsEditing(true)}>
                      <Pencil className="h-3.5 w-3.5" />
                      Edit
                    </Button>
                    {onDelete && (
                      <Button
                        variant="danger"
                        size="sm"
                        iconOnlyOnMobile
                        disabled={deleting}
                        onClick={() => setShowDeleteConfirm(true)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        {deleting ? 'Deleting...' : 'Delete'}
                      </Button>
                    )}
                    <div className="h-5 w-px bg-gray-200 dark:bg-gray-700 mx-0.5" />
                    <Button
                      variant={showComments ? 'primary' : 'secondary'}
                      size="sm"
                      iconOnlyOnMobile
                      onClick={() => setShowComments(!showComments)}
                    >
                      <MessageSquare className="h-3.5 w-3.5" />
                      {showComments ? 'Hide' : 'Comments'}{item.comment_count > 0 ? ` (${item.comment_count})` : ''}
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      iconOnlyOnMobile
                      onClick={() => {
                        onClose();
                        navigate(`/roadmap/${workspaceId}/item/${item.id}`);
                      }}
                      title="Open full page"
                    >
                      <Maximize2 className="h-3.5 w-3.5" />
                      Full Page
                    </Button>
                  </div>
                )}

                {/* Item State */}
                {item.item_state && item.item_state !== 'active' && (
                  <div className="mt-4 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                    <Badge variant={item.item_state === 'blocked' ? 'error' : item.item_state === 'at_risk' ? 'warning' : 'gray'}>
                      {item.item_state.replace(/_/g, ' ')}
                    </Badge>
                  </div>
                )}

                {/* Assignees */}
                {item.assignees && item.assignees.length > 0 && (
                  <div className="mt-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="h-4 w-4 text-gray-400" />
                      <span className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Assignees</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {item.assignees.map(a => (
                        <div key={a.id} className="flex items-center gap-2 rounded-full bg-gray-100 dark:bg-gray-700 px-3 py-1">
                          <div className="h-5 w-5 rounded-full bg-brand-200 dark:bg-brand-800 flex items-center justify-center text-[10px] font-medium text-brand-700 dark:text-brand-300">
                            {a.name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <span className="text-sm text-gray-700 dark:text-gray-300">{a.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Dependencies */}
                {((item.blocks && item.blocks.length > 0) || (item.blocked_by && item.blocked_by.length > 0)) && (
                  <div className="mt-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Link2 className="h-4 w-4 text-gray-400" />
                      <span className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Dependencies</span>
                    </div>
                    {item.blocks && item.blocks.length > 0 && (
                      <div className="mb-2">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Blocks:</span>
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {item.blocks.map(dep => (
                            <Badge key={dep.dependency_id} variant="error">
                              {dep.title}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {item.blocked_by && item.blocked_by.length > 0 && (
                      <div>
                        <span className="text-xs text-gray-500 dark:text-gray-400">Blocked by:</span>
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {item.blocked_by.map(dep => (
                            <Badge key={dep.dependency_id} variant="warning">
                              {dep.title}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* External Links */}
                {item.external_links && item.external_links.length > 0 && (
                  <div className="mt-4">
                    <div className="flex items-center gap-2 mb-2">
                      <ExternalLink className="h-4 w-4 text-gray-400" />
                      <span className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Links</span>
                    </div>
                    <div className="space-y-1.5">
                      {item.external_links.map((link, idx) => (
                        <a
                          key={idx}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-sm text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300 truncate"
                        >
                          <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" />
                          {link.label || link.url}
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Reactions */}
                <div className="mt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <SmilePlus className="h-4 w-4 text-gray-400" />
                    <span className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Reactions</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {reactions.map(r => (
                      <button
                        key={r.emoji}
                        type="button"
                        onClick={async () => {
                          try {
                            const updated = await itemApi.react(workspaceId, item.id, r.emoji);
                            setReactions(updated);
                            onUpdate({ ...item, reactions: updated });
                          } catch (err) {
                            console.error('Failed to toggle reaction:', err);
                          }
                        }}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors ${
                          user && r.user_ids.includes(user.id)
                            ? 'border-brand-300 bg-brand-50 dark:border-brand-700 dark:bg-brand-950/30'
                            : 'border-gray-200 bg-gray-50 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700'
                        }`}
                      >
                        <span className="text-base leading-none">{r.emoji}</span>
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-300">{r.count}</span>
                      </button>
                    ))}
                    <EmojiPicker onSelect={async (emoji) => {
                      try {
                        const updated = await itemApi.react(workspaceId, item.id, emoji);
                        setReactions(updated);
                        onUpdate({ ...item, reactions: updated });
                      } catch (err) {
                        console.error('Failed to add reaction:', err);
                      }
                    }} />
                  </div>
                </div>
              </div>
            )}

            {/* Attachments */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Paperclip className="h-4 w-4 text-gray-400" />
                  <span className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Attachments {attachments.length > 0 && `(${attachments.length})`}
                  </span>
                </div>
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileUpload}
                    className="hidden"
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.md,.zip,.tar,.gz,.json,.xml"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    <Upload className="h-3.5 w-3.5" />
                    {uploading ? 'Uploading...' : 'Upload'}
                  </Button>
                </div>
              </div>
              {attachments.length > 0 && (
                <div className="space-y-2">
                  {attachments.map(att => (
                    <div
                      key={att.id}
                      className="flex items-center gap-3 p-2 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800"
                    >
                      {isImageMime(att.mime_type) ? (
                        <img
                          src={att.url}
                          alt={att.original_name}
                          className={`h-10 w-10 rounded object-cover flex-shrink-0 ${att.path ? 's3-asset' : ''}`}
                          {...(att.path ? { 'data-s3-path': att.path, 'data-s3-expires': Date.now().toString() } : {})}
                        />
                      ) : (
                        <div className="h-10 w-10 rounded bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                          <Paperclip className="h-4 w-4 text-gray-500" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <a
                          href={att.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`text-sm font-medium text-gray-900 dark:text-white hover:text-brand-600 dark:hover:text-brand-400 truncate block ${att.path ? 's3-asset' : ''}`}
                          {...(att.path ? { 'data-s3-path': att.path, 'data-s3-expires': Date.now().toString() } : {})}
                        >
                          {att.original_name}
                        </a>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {formatFileSize(att.size)}
                        </span>
                      </div>
                      <button
                        onClick={() => handleDeleteAttachment(att.id)}
                        className="p-1 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                        title="Delete attachment"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
        </div>

        {/* Comments slide-out panel */}
        <div
          className={`transition-all duration-300 ease-in-out overflow-hidden ${
            showComments ? 'w-[340px] min-w-[340px] border-l border-gray-200 dark:border-gray-700' : 'w-0 min-w-0'
          }`}
        >
          <div className="w-[340px] h-full flex flex-col overflow-hidden">
            <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Comments</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowComments(false)}
                className="flex h-7 w-7 items-center justify-center rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:text-gray-300 dark:hover:bg-gray-700 transition-colors"
                title="Hide comments"
              >
                <PanelRightClose className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 min-h-0 px-4 py-4">
              <CommentSection 
                workspaceId={workspaceId} 
                itemId={item.id} 
                showHeader={false}
                layout="panel"
                onCommentCountChange={(count) => {
                  const updatedItem = { ...item, comment_count: count };
                  onUpdate(updatedItem);
                }}
              />
            </div>
          </div>
        </div>

        </div>
      </div>
      {onDelete && (
        <ConfirmDialog
          open={showDeleteConfirm}
          onOpenChange={setShowDeleteConfirm}
          title="Delete Item"
          description="Are you sure you want to delete this item? This action cannot be undone."
          confirmLabel="Delete"
          variant="danger"
          loading={deleting}
          onConfirm={async () => {
            try {
              setDeleting(true);
              await itemApi.delete(workspaceId, item.id);
              onDelete(item.id);
              onClose();
            } catch (err) {
              console.error('Failed to delete item:', err);
            } finally {
              setDeleting(false);
              setShowDeleteConfirm(false);
            }
          }}
        />
      )}
    </div>
  );
}
